<script setup lang="ts">
/**
 * LanguageSwitcher - Language selector dropdown
 *
 * Displays current language and allows switching between available languages.
 */
import { ref, onMounted, onUnmounted } from 'vue'
import { useLocaleStore } from '@/stores/locale'
import type { Language } from '@/i18n'

const localeStore = useLocaleStore()
const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

function toggleDropdown(event: Event) {
  event.stopPropagation()
  isOpen.value = !isOpen.value
}

function closeDropdown() {
  isOpen.value = false
}

async function selectLanguage(locale: Language, event: Event) {
  event.stopPropagation()
  await localeStore.changeLocale(locale)
  closeDropdown()
}

// Close dropdown when clicking outside
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (dropdownRef.value && !dropdownRef.value.contains(target)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <!-- Current Language Button -->
    <button
      @click="toggleDropdown($event)"
      class="flex items-center gap-1.5 px-2 py-1.5 text-sm text-charcoal hover:text-primary-500 transition-colors border border-gray-200 rounded-lg"
      :class="{ 'opacity-50': localeStore.isLoading }"
      :disabled="localeStore.isLoading"
    >
      <img
        :src="`https://flagcdn.com/20x15/${localeStore.currentCountryCode}.png`"
        :srcset="`https://flagcdn.com/40x30/${localeStore.currentCountryCode}.png 2x`"
        :alt="localeStore.currentLanguageName"
        class="w-5 h-auto rounded-sm"
      />
      <span class="font-medium uppercase">{{ localeStore.currentLocale }}</span>
      <svg
        class="w-3 h-3 transition-transform"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
      >
        <button
          v-for="locale in localeStore.availableLocales"
          :key="locale"
          @click="selectLanguage(locale, $event)"
          class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
          :class="{
            'text-primary-500 bg-primary-50': locale === localeStore.currentLocale,
            'text-charcoal': locale !== localeStore.currentLocale
          }"
        >
          <img
            :src="`https://flagcdn.com/20x15/${localeStore.getCountryCode(locale)}.png`"
            :srcset="`https://flagcdn.com/40x30/${localeStore.getCountryCode(locale)}.png 2x`"
            :alt="localeStore.getLanguageName(locale)"
            class="w-5 h-auto rounded-sm"
          />
          <span class="font-medium uppercase w-6">{{ locale }}</span>
          <span>{{ localeStore.getLanguageName(locale) }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>
